import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Save, Pencil, X, Wallet, TrendingUp, Percent, Coins, FileText } from "lucide-react";
import { toast } from "sonner";
import RemunerationSection, { serializeRemuneration } from "./RemunerationSection";

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Rémunération d'un collaborateur — vue synthèse + édition (admin).
 * Props :
 *   - collaborateur : document utilisateur complet.
 *   - onSaved(updated) : callback avec le doc mis à jour (pour setCollab dans le parent).
 *   - readOnly : masque le bouton Modifier (utilisé sur Mon profil).
 */
export default function RemunerationCard({ collaborateur, onSaved, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => hydrate(collaborateur));

  const openEdit = () => {
    setForm(hydrate(collaborateur));
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = serializeRemuneration(form);
      const { data } = await api.put(`/collaborateurs/${collaborateur.id}`, payload);
      toast.success("Rémunération mise à jour");
      setEditing(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const mode = collaborateur?.remuneration_variable_mode;
  const hasFixed = collaborateur?.remuneration_fixed_ht !== null
    && collaborateur?.remuneration_fixed_ht !== undefined
    && collaborateur?.remuneration_fixed_ht !== "";
  const hasVariable = mode === "percentage" || mode === "amount";

  return (
    <div className="space-y-4" data-testid="remuneration-card">
      {editing ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">Modifier la rémunération</h3>
              <p className="text-xs font-mono text-zinc-500">Les champs vides indiquent l&apos;absence de rémunération pour ce volet.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={cancel} disabled={saving} data-testid="remun-cancel-btn">
                <X size={13} className="mr-1" /> Annuler
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                data-testid="remun-save-btn"
              >
                <Save size={13} className="mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
          <RemunerationSection form={form} setForm={setForm} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900">
                {readOnly ? "Ma rémunération" : "Rémunération du collaborateur"}
              </h3>
              <p className="text-xs font-mono text-zinc-500">
                {readOnly
                  ? "Détail de votre rémunération fixe et/ou variable — consultation uniquement."
                  : "Composée d'un fixe forfaitaire mensuel et/ou d'une variable indexée sur les ventes."}
              </p>
            </div>
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={openEdit} data-testid="remun-edit-btn">
                <Pencil size={13} className="mr-1" /> Modifier
              </Button>
            )}
          </div>

          {!hasFixed && !hasVariable ? (
            <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center bg-zinc-50" data-testid="remun-empty-state">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-200 mb-2">
                <Wallet size={18} className="text-zinc-500" />
              </div>
              <p className="text-sm text-zinc-700">
                {readOnly
                  ? "Aucune rémunération configurée sur votre profil."
                  : "Aucune rémunération configurée pour ce collaborateur."}
              </p>
              {!readOnly && (
                <p className="text-xs font-mono text-zinc-500 mt-1">Cliquez sur « Modifier » pour définir un fixe et/ou un variable.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SummaryCard
                icon={<Wallet size={16} />}
                accent="#10B981"
                label="Fixe forfaitaire mensuel"
                value={hasFixed ? money(collaborateur.remuneration_fixed_ht) : "—"}
                sub={hasFixed ? "€ HT / mois" : "Non défini"}
                tid="remun-summary-fixed"
              />
              <SummaryCard
                icon={mode === "percentage" ? <Percent size={16} /> : mode === "amount" ? <Coins size={16} /> : <TrendingUp size={16} />}
                accent="#F97316"
                label="Rémunération variable"
                value={
                  mode === "percentage"
                    ? `${Number(collaborateur.remuneration_variable_percentage || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
                    : mode === "amount"
                      ? money(collaborateur.remuneration_variable_amount)
                      : "—"
                }
                sub={
                  mode === "percentage"
                    ? "du CA HT généré par période"
                    : mode === "amount"
                      ? "€ HT / vente réalisée"
                      : "Non défini"
                }
                tid="remun-summary-variable"
              />
            </div>
          )}

          {!readOnly && collaborateur?.remuneration_notes && (
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText size={12} className="text-zinc-500" />
                <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">Notes internes</span>
              </div>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap" data-testid="remun-summary-notes">
                {collaborateur.remuneration_notes}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function hydrate(c = {}) {
  return {
    remuneration_fixed_ht: c.remuneration_fixed_ht ?? "",
    remuneration_variable_mode:
      c.remuneration_variable_mode && c.remuneration_variable_mode !== ""
        ? c.remuneration_variable_mode
        : "none",
    remuneration_variable_percentage: c.remuneration_variable_percentage ?? "",
    remuneration_variable_amount: c.remuneration_variable_amount ?? "",
    remuneration_notes: c.remuneration_notes || "",
  };
}

function SummaryCard({ icon, accent, label, value, sub, tid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4 flex items-start gap-3" data-testid={tid}>
      <div
        className="h-9 w-9 rounded-sm text-white inline-flex items-center justify-center shrink-0"
        style={{ backgroundColor: accent }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">{label}</div>
        <div className="text-xl font-semibold font-mono text-zinc-900 mt-0.5">{value}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
